import React from "react";
import { Table } from "react-bootstrap";
import { observer } from "mobx-react";
import Plus from "../../../../../images/products/plus.svg";
import Close from "../../../../../images/products/close.svg";
import { PriceTierInputData, PriceTierInputOptions } from "../models";
import { PricingModel } from "../enums";
import {
  CustomLabel,
  FlexContainer,
  IconButton,
  InfoIcon,
  Input,
  nameof,
  StyledTable,
  theme,
} from "@mindmemobile/mm-shared-global";
import { DescriptionCell, LabelCell, PricingTableWrapper } from "../styles";
import { AccountSettingsStore } from "../store";

type IProps = {
  tier: PriceTierInputData;
  onUpdateTier: (data: PriceTierInputData) => void;
  totalPrice: number;
  pricingType: string;
  renderAsLabels?: boolean;
};

@observer
export default class TierPricingComponent extends React.Component<IProps> {
  onEndValueChange(value: string, idx: number) {
    if (idx + 1 === this.props.tier.options.length) return;
    const clonedTiers = this.props.tier.options.map((a) =>
      Object.assign({}, a)
    );
    if (value.length > 1 && value.startsWith("0")) {
      value = value.slice(1);
    }
    if (isNaN(parseInt(value))) {
      clonedTiers[idx].endValue = "0";
    } else {
      clonedTiers[idx].endValue = value;
    }
    //this.props.onUpdateOption(new PriceTierInputOptions(clonedTiers[idx]), idx);
    this.props.onUpdateTier(
      new PriceTierInputData({ ...this.props.tier, options: clonedTiers })
    );
  }

  formatAmount = (value: string): string => {
    if (parseFloat(value) < 0 || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return AccountSettingsStore.formatAmountWithoutCommas(
      parseFloat(value.toString().replace(/,/g, "") || "0.00"),
      true
    );
  };

  onPercentageChange(value: string, idx: number) {
    const clonedTiers = this.props.tier.options.map((a) =>
      Object.assign({}, a)
    );
    clonedTiers[idx].discountPercentage = value;
    // this.props.onUpdateOption(new PriceTierInputOptions(clonedTiers[idx]), idx);
    this.props.onUpdateTier(
      new PriceTierInputData({ ...this.props.tier, options: clonedTiers })
    );
  }

  onPriceChange(value: string, isBlur?: boolean) {
    if (isBlur) {
      value = this.formatAmount(value);
    }
    this.props.onUpdateTier(
      new PriceTierInputData({ ...this.props.tier, price: value })
    );
  }

  onSetUpFeeChange(value: string, isBlur?: boolean) {
    if (isBlur) {
      value = this.formatAmount(value);
    }
    this.props.onUpdateTier(
      new PriceTierInputData({ ...this.props.tier, setUpFee: value })
    );
  }

  onOriginalPriceChange(value: string, isBlur?: boolean) {
    if (isBlur) {
      value = this.formatAmount(value);
    }
    this.props.onUpdateTier(
      new PriceTierInputData({ ...this.props.tier, originalPrice: value })
    );
  }

  toggleOriginalPrice(value: boolean) {
    this.props.onUpdateTier(
      new PriceTierInputData({ ...this.props.tier, showOriginalPrice: value })
    );
  }

  onAddRow() {
    const clonedOptions = this.props.tier.options.map((a) =>
      Object.assign({}, a)
    );
    const lastIndex = clonedOptions.length - 1;

    let startValue, endValue;

    if (lastIndex === 0) {
      startValue = 1;
      endValue = 9;
    } else {
      const lastButOneOption = clonedOptions[lastIndex - 1];
      startValue = parseInt(lastButOneOption.endValue) + 1;
      endValue = startValue + 1;
    }

    const newVolume = new PriceTierInputOptions({
      startValue: startValue,
      endValue: endValue.toString(),
      discountPercentage: "",
    });

    clonedOptions.splice(lastIndex, 0, newVolume);
    this.props.onUpdateTier(
      new PriceTierInputData({ ...this.props.tier, options: clonedOptions })
    );
  }

  hasError(index: number, field: string): boolean {
    const { errors } = this.props.tier;
    return (
      errors?.find((x) => x.index == index && x.field === field) !== undefined
    );
  }
  getColumnLabel = (index: number) => {
    if (this.props.pricingType === PricingModel.Tiered && index == 0) {
      return "FOR THE FIRST";
    }
    if (this.props.pricingType === PricingModel.Tiered) {
      return "FOR THE NEXT";
    }
    if (this.props.pricingType === PricingModel.Volume) {
      return "TOTAL UNITS";
    }
  };

  onDeleteRow(idx: number) {
    const clonedOptions = this.props.tier.options.map((a) =>
      Object.assign({}, a)
    );
    clonedOptions.splice(idx, 1);

    if (idx == clonedOptions.length - 1) {
      clonedOptions[clonedOptions.length - 1].startValue--;
    }
    clonedOptions[clonedOptions.length - 1].endValue = "∞";
    const clonedTierIndexes = this.props.tier.tierErrorIndexes.map((a) =>
      Object.assign({}, a)
    );
    const errorIndex = clonedTierIndexes.findIndex((x) => x === idx);
    if (errorIndex > -1) {
      clonedTierIndexes.splice(errorIndex, 1);
    }
    this.props.onUpdateTier(
      new PriceTierInputData({
        ...this.props.tier,
        options: clonedOptions,
        tierErrorIndexes: clonedTierIndexes,
      })
    );
  }

  get topRow() {
    if (this.props.renderAsLabels) return null;
    return (
      <tr>
        <th colSpan={6}>
          <FlexContainer justifyContent="end">
            <IconButton
              className="add-package-btn"
              color={theme.palette.info.dark}
              type="button"
              onClick={() => this.onAddRow()}
              gap="4px"
              height="23px"
            >
              <img src={Plus} alt="Add Row" />
              <span> Add Row</span>
            </IconButton>
          </FlexContainer>
        </th>
      </tr>
    );
  }

  render(): React.ReactNode {
    const {
      price,
      options,
      showOriginalPrice,
      originalPrice,
      showSetUpFee,
      setUpFee,
    } = this.props.tier;
    const { renderAsLabels } = this.props;
    return (
      <PricingTableWrapper>
        {showSetUpFee && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel lineHeight="40px" fontSize="16px">
                {" "}
                Setup Fee
              </CustomLabel>
              <InfoIcon />
            </LabelCell>
            <DescriptionCell>
              <FlexContainer
                spacing={15}
                fxLayout="row"
                className="dollar-usd-input"
              >
                {renderAsLabels && (
                  <>
                    {" "}
                    {setUpFee
                      ? `$${AccountSettingsStore.formatAmount(parseFloat(setUpFee), true)}`
                      : "-"}
                  </>
                )}
                {!renderAsLabels && (
                  <div>
                    <Input
                      value={setUpFee}
                      error={this.hasError(
                        -1,
                        nameof<PriceTierInputData>("setUpFee")
                      )}
                      id={`txt_tier_price`}
                      type="text"
                      onChange={(e, isBlur) =>
                        this.onSetUpFeeChange(e.target.value, isBlur)
                      }
                    />
                  </div>
                )}
              </FlexContainer>
            </DescriptionCell>
          </FlexContainer>
        )}
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel>Price</CustomLabel>
            <InfoIcon />
          </LabelCell>
          <DescriptionCell>
            <FlexContainer spacing={15}>
              <div>
                {renderAsLabels && (
                  <>
                    $
                    {AccountSettingsStore.formatAmount(parseFloat(price), true)}
                  </>
                )}
                {!renderAsLabels && (
                  <FlexContainer
                    spacing={15}
                    fxLayout="row"
                    className="dollar-usd-input"
                  >
                    <div>
                      <Input
                        value={price}
                        error={this.hasError(
                          -1,
                          nameof<PriceTierInputData>("price")
                        )}
                        id={`txt_tier_price`}
                        type="text"
                        onChange={(e, isBlur) =>
                          this.onPriceChange(e.target.value, isBlur)
                        }
                      />
                    </div>
                  </FlexContainer>
                )}
              </div>
              {!renderAsLabels && (
                <Input
                  value={showOriginalPrice}
                  id={`chk_show_originalPrice`}
                  type="checkbox"
                  label="Show Original Price"
                  onChange={(e) => this.toggleOriginalPrice(e.target.checked)}
                />
              )}
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        {showOriginalPrice && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel> Original Price</CustomLabel>
              <InfoIcon />
            </LabelCell>
            <DescriptionCell>
              <div>
                {!renderAsLabels && (
                  <FlexContainer
                    spacing={15}
                    fxLayout="row"
                    className="dollar-usd-input"
                  >
                    <div>
                      <Input
                        value={originalPrice}
                        error={this.hasError(
                          -2,
                          nameof<PriceTierInputData>("originalPrice")
                        )}
                        id={`txt_tier_original_price`}
                        type="text"
                        onChange={(e, isBlur) =>
                          this.onOriginalPriceChange(e.target.value, isBlur)
                        }
                      />
                    </div>
                  </FlexContainer>
                )}
                {renderAsLabels && (
                  <>
                    {" "}
                    {originalPrice
                      ? `$${AccountSettingsStore.formatAmount(parseFloat(originalPrice), true)}`
                      : "-"}
                  </>
                )}
              </div>
            </DescriptionCell>
          </FlexContainer>
        )}
        <FlexContainer className="label-description-row">
          <LabelCell></LabelCell>
          <DescriptionCell>
            <StyledTable className="tier-table">
              <Table bsPrefix="styled-table">
                <thead>
                  {this.topRow}
                  <tr>
                    <th></th>
                    <th>FIRST</th>
                    <th>LAST</th>
                    <th>DISCOUNT</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {options &&
                    options.map((t, index) => {
                      const allowDelete = index > 0;
                      const endError =
                        this.hasError(
                          index,
                          nameof<PriceTierInputOptions>("endValue")
                        ) ||
                        this.props.tier.tierErrorIndexes?.indexOf(index) > -1;

                      return (
                        <tr key={index}>
                          <td>{this.getColumnLabel(index)}</td>
                          <td>{t.startValue}</td>
                          <td>
                            {renderAsLabels && <>{t.endValue}</>}
                            {!renderAsLabels && (
                              <Input
                                value={t.endValue}
                                error={endError}
                                id={`txt_end_${index}`}
                                type="text"
                                disabled={options.length === index + 1}
                                onChange={(e) =>
                                  this.onEndValueChange(e.target.value, index)
                                }
                              />
                            )}
                          </td>
                          {renderAsLabels && (
                            <>
                              <td>{t.discountPercentage} %</td>
                              <td></td>
                              <td></td>
                            </>
                          )}
                          {!renderAsLabels && (
                            <td>
                              <div>
                                <FlexContainer
                                  justifyContent="center"
                                  fxLayout="row"
                                  className="percent-input"
                                >
                                  <div>
                                    <Input
                                      error={this.hasError(
                                        index,
                                        nameof<PriceTierInputOptions>(
                                          "discountPercentage"
                                        )
                                      )}
                                      value={t.discountPercentage}
                                      id={`txt_percentage_${index}`}
                                      type="text"
                                      onChange={(e) =>
                                        this.onPercentageChange(
                                          e.target.value,
                                          index
                                        )
                                      }
                                      disabled={index === 0}
                                      placeholder="0.00"
                                    />
                                  </div>
                                </FlexContainer>
                              </div>
                            </td>
                          )}
                          {!renderAsLabels && (
                            <>
                              <td className="displayed-price">
                                {t.cost(this.props.totalPrice)}
                              </td>
                              <td>
                                {allowDelete && (
                                  <button
                                    className="delete-row-btn"
                                    onClick={() => this.onDeleteRow(index)}
                                  >
                                    <img src={Close} alt="Delete Row" />
                                  </button>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </StyledTable>
          </DescriptionCell>
        </FlexContainer>
      </PricingTableWrapper>
    );
  }
}
